import { useMemo } from 'react'

import { api } from '@/shared/lib/api'

import type { FinishStepInput, LevelPlayAdapter } from './types'
import type { Step } from '../stepTypes'

/**
 * Adapter do quiz.
 *
 * As perguntas chegam por `quiz_questions/next`, então a fonte é assíncrona e
 * pode crescer durante a execução. Quem busca é o wrapper — o adapter só
 * declara o estado desse carregamento para o motor.
 *
 * A finalização usa `progress/finish_resource`, cujo envelope é diferente do da
 * jornada: precisa de `resource_type`/`resource_id` e aninha a resposta em
 * `meta`. Resolver o `resource_id` a partir do `uid` é responsabilidade daqui,
 * porque é detalhe do contrato do quiz — o motor conhece só o `uid`.
 */
export const useQuizAdapter = ({
  eventSlug,
  steps,
  isLoading,
  isExhausted,
  allowWrongAnswers,
  maxHearts,
  resolveQuestionId
}: {
  eventSlug: string
  steps: Step[]
  isLoading: boolean
  isExhausted: boolean
  allowWrongAnswers: boolean
  maxHearts: number
  /** `uid` da pergunta → `id` numérico, que é o que `finish_resource` exige. */
  resolveQuestionId: (uid: string) => number | undefined
}): LevelPlayAdapter =>
  useMemo(
    () => ({
      domain: 'quiz' as const,

      stepSource: { kind: 'async' as const, steps, isLoading, isExhausted },

      allowWrongAnswers,
      maxHearts,

      async finishStep({ uid, payload }: FinishStepInput) {
        const record =
          payload.kind === 'voucher'
            ? { voucher_id: payload.voucherId }
            : {
                resource_type: 'QuizQuestion',
                resource_id: resolveQuestionId(uid),
                meta: {
                  answer: payload.answer,
                  right_answer: payload.answerIsRight
                }
              }

        await api.post(
          `/user/digital_products/${eventSlug}/progress/finish_resource`,
          { record }
        )
      },

      exitPath: () => '/quizzes'
    }),
    [
      eventSlug,
      steps,
      isLoading,
      isExhausted,
      allowWrongAnswers,
      maxHearts,
      resolveQuestionId
    ]
  )
